import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Pagination, Table } from 'react-bootstrap';
import axios from 'axios';
import { authContext } from '../Context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import sinimagen from '../Categorias/sinimagen.gif';
import './ProximosIngresos.css';

const apiProductos = process.env.REACT_APP_API_PRODUCTOS;

const ProximosIngresos = () => {
    const country = process.env.REACT_APP_COUNTRY;
    const navigate = useNavigate();
    const { token } = useContext(authContext);
    const [t] = useTranslation("global");

    const [articulos, setArticulos] = useState([]);
    const [load, setLoad] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [largoTotal, setLargoTotal] = useState(0);
    const elementosPorPagina = 20;

    // Solo permitir acceso para UY
    useEffect(() => {
        if (country !== 'UY') {
            navigate('/');
        }
    }, [country, navigate]);

    // Obtener datos del endpoint
    const obtenerDatos = async () => {
        try {
            setError(null);

            const config = {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                timeout: 10000
            };

            // TODO: Reemplazar con el endpoint real cuando esté disponible
            const response = await axios.get(
                `${apiProductos}/endpoint-pendiente/proximos-ingresos`,
                config
            );

            if (!response?.data?.articulos || response.data.articulos.length === 0) {
                setArticulos([]);
                setLargoTotal(0);
                setLoad(true);
                return;
            }

            const todosLosArticulos = response.data.articulos;
            setLargoTotal(todosLosArticulos.length);

            // Paginación local
            const inicio = (currentPage - 1) * elementosPorPagina;
            const fin = inicio + elementosPorPagina;
            setArticulos(todosLosArticulos.slice(inicio, fin));
            setLoad(true);

        } catch (err) {
            console.error('Error al obtener próximos ingresos:', err);
            setError(err.message || 'Error al cargar los datos');
            setArticulos([]);
        }
    };

    useEffect(() => {
        if (country === 'UY') {
            obtenerDatos();
        }
    }, [currentPage]);

    // Renderizar link al producto
    const renderProductLink = (articulo) => {
        const linkProps = {
            style: {
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.25rem 0.5rem',
                fontSize: '0.75rem',
                justifyContent: 'center',
                minWidth: '120px',
                textDecoration: 'none',
                color: '#dc3545',
                border: '1px solid #dc3545',
                borderRadius: '0.25rem',
                backgroundColor: 'transparent',
                transition: 'background-color 0.2s, color 0.2s',
            },
            to: `/detalle?sku=${articulo.Alias || ''}`,
            onClick: (event) => {
                if (event.button === 1) {
                    window.open(`/detalle?sku=${articulo.Alias || ''}`, "_blank");
                    event.preventDefault();
                }
            },
            onMouseEnter: (e) => {
                e.currentTarget.style.backgroundColor = '#dc3545';
                e.currentTarget.style.color = 'white';
            },
            onMouseLeave: (e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#dc3545';
            }
        };

        return (
            <Link {...linkProps}>
                {t("producto.ver-producto")}
            </Link>
        );
    };

    // Paginación
    const renderPagination = () => {
        const totalPages = Math.ceil(largoTotal / elementosPorPagina);
        const paginas = [];
        const paginasMostradas = 5;
        let inicio = Math.max(1, currentPage - Math.floor(paginasMostradas / 2));
        const fin = Math.min(totalPages, inicio + paginasMostradas - 1);

        inicio = Math.max(1, fin - paginasMostradas + 1);

        if (inicio > 1) {
            paginas.push(
                <Pagination.First key="first" onClick={() => setCurrentPage(1)} />
            );
            if (inicio > 2) {
                paginas.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        for (let i = inicio; i <= fin; i++) {
            paginas.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => setCurrentPage(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (fin < totalPages) {
            if (fin < totalPages - 1) {
                paginas.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            paginas.push(
                <Pagination.Last
                    key="last"
                    onClick={() => setCurrentPage(totalPages)}
                />
            );
        }

        return paginas;
    };

    // Formatear fecha
    const formatFecha = (fecha) => {
        if (!fecha) return 'N/A';
        try {
            const date = new Date(fecha);
            return date.toLocaleDateString('es-UY', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return fecha;
        }
    };

    if (country !== 'UY') return null;

    return (
        <div className="proximos-ingresos-container">
            <Container fluid className="py-5">
                <Row className="justify-content-center">
                    <Col xs={12} md={10} lg={9}>
                        <h2 className="proximos-ingresos-titulo text-center mb-4">
                            {t("proximosIngresos.titulo")}
                        </h2>

                        {!load ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Cargando...</span>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="alert alert-danger text-center">
                                <p>{error}</p>
                                <button className="btn btn-primary" onClick={obtenerDatos}>
                                    {t("proximosIngresos.reintentar")}
                                </button>
                            </div>
                        ) : articulos.length > 0 ? (
                            <div className="taleShadow bg-light">
                                <div className="table-container-proximos">
                                    <Table hover variant="light">
                                        <tbody>
                                            {articulos.map((articulo, idx) => (
                                                <tr key={`${articulo.Alias || idx}-${idx}`}>
                                                    <td>
                                                        <Link
                                                            to={`/detalle?sku=${articulo.Alias}`}
                                                            style={{ textDecoration: 'none', color: 'inherit' }}
                                                        >
                                                            <h6 className="table-name-product hover-imagen">
                                                                {articulo.Nombre || 'Nombre no disponible'}
                                                                <span className="mini-image-tooltip">
                                                                    <img
                                                                        src={
                                                                            articulo.Imagenes
                                                                                ? process.env.PUBLIC_URL + `/articulos/thumbs/${encodeURIComponent(String(articulo.Imagenes.split(',')[0]))}`
                                                                                : sinimagen
                                                                        }
                                                                        alt="vista previa"
                                                                    />
                                                                </span>
                                                            </h6>
                                                        </Link>

                                                        <div className="articulo-info-linea">
                                                            <p className="numero-de-parte">
                                                                Marca: {articulo.Marca || 'N/A'}
                                                            </p>

                                                            <p className="numero-de-parte">
                                                                SKU: {articulo.Alias || 'N/A'}
                                                            </p>

                                                            <p className="numero-de-parte fecha-ingreso">
                                                                {t("proximosIngresos.fecha-ingreso")}: {formatFecha(articulo.Fecha_Ingreso)}
                                                            </p>
                                                        </div>
                                                    </td>
                                                    <td className="table-botonera">
                                                        <div className="button-container">
                                                            <div className="button-contenedor-principal">
                                                                <div className="logged-buttons-categorias">
                                                                    <div className="ver-producto-container">
                                                                        {renderProductLink(articulo)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Paginación */}
                                <Pagination className="justify-content-center mt-3">
                                    <Pagination.Prev
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(prev => prev - 1)}
                                    />
                                    {renderPagination()}
                                    <Pagination.Next
                                        disabled={currentPage === Math.ceil(largoTotal / elementosPorPagina)}
                                        onClick={() => setCurrentPage(prev => prev + 1)}
                                    />
                                </Pagination>

                                <div className="text-center mt-2 mb-3">
                                    Página {currentPage} de {Math.ceil(largoTotal / elementosPorPagina)}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <p>{t("proximosIngresos.sin-resultados")}</p>
                                <button className="btn btn-primary" onClick={() => navigate('/')}>
                                    {t("producto.volver-inicio")}
                                </button>
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default ProximosIngresos;
